"use client";

import React from "react";
import { MapPin, Phone, Mail, CloudUpload, Loader, CircleCheck, CircleAlert } from "lucide-react";
import { Link } from "@/components/common/Link";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
const CONTACT_INFO = {
  phone: "778-385-3007",
  email: "JunkCarPickUpBC@gmail.com",
  address: "British Columbia, Canada",
  businessName: "Junk Car Pick Up BC"
};
const SERVICE_OPTIONS = ["Cash for Junk Car", "Free Pickup Request", "Scrap Car Recycling Info", "General Inquiry"];
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in duration-500">
        <div className="size-16 rounded-full bg-primary/20 flex items-center justify-center mb-4">
          <CircleCheck className="size-8 text-primary" />
        </div>
        <h3 className="text-2xl font-bold text-foreground">Request Received!</h3>
        <p className="text-muted-foreground max-w-md">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-6 border-primary/50 hover:bg-primary/10">
          Send Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="name" rules={{
        required: "Please enter your name"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90">Your Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" className="bg-background/50 border-border/50 focus-visible:border-primary" {...field} />
              </FormControl>
              <FormDescription className="text-xs text-muted-foreground/70">First and last name</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone number is required",
        pattern: {
          value: /^[0-9-+() ]{10,15}$/,
          message: "Please enter a valid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="778-385-3007" className="bg-background/50 border-border/50 focus-visible:border-primary" {...field} />
              </FormControl>
              <FormDescription className="text-xs text-muted-foreground/70">We'll call you back quickly</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="email" rules={{
      required: "Email address is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/90">Email Address</FormLabel>
            <FormControl>
              <Input type="email" placeholder="john@example.com" className="bg-background/50 border-border/50 focus-visible:border-primary" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="service_interest" rules={{
      required: "Please select a reason for contact"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/90">Reason for Contact</FormLabel>
            <FormControl>
              <div className="relative">
                <select className="flex h-9 w-full appearance-none rounded-md border border-border/50 bg-background/50 px-3 py-1 text-base shadow-xs transition-colors focus-visible:border-primary focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm dark:bg-input/30 text-foreground" {...field}>
                  <option value="" disabled className="bg-background text-muted-foreground">Select an option</option>
                  {SERVICE_OPTIONS.map((option, i) => <option key={option} value={option} data-index={i} className="bg-background text-foreground">
                      {option}
                    </option>)}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                  <svg className="h-4 w-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/90">Message</FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us about your vehicle (make, model, year, condition)..." className="min-h-[120px] bg-background/50 border-border/50 focus-visible:border-primary resize-y" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <div className="space-y-2">
        <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-foreground/90">
          Vehicle Photos (Optional)
        </label>
        <div className="border-2 border-dashed border-border/50 rounded-lg p-6 flex flex-col items-center justify-center text-center bg-background/30 hover:bg-background/50 transition-colors cursor-pointer group">
          <div className="p-3 bg-primary/10 rounded-full mb-3 group-hover:bg-primary/20 transition-colors">
            <CloudUpload className="size-6 text-primary" />
          </div>
          <p className="text-sm font-medium text-foreground">Click to upload or drag and drop</p>
          <p className="text-xs text-muted-foreground mt-1">SVG, PNG, JPG or GIF (max. 10MB)</p>
        </div>
      </div>

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-start gap-3">
          <CircleAlert className="size-5 text-destructive shrink-0 mt-0.5" />
          <p className="text-sm text-destructive/90">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-base font-bold tracking-wide shadow-[0_0_15px_rgba(132,255,0,0.15)] hover:shadow-[0_0_25px_rgba(132,255,0,0.25)] transition-all">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Processing Request...
          </> : 'Get Instant Quote'}
      </Button>
    </Form>;
}
export default function ReachOurTeam() {
  return <section id="reach-our-team" className="dark bg-background text-foreground py-20 px-4 md:px-8 lg:px-12 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-5%] w-[40%] h-[40%] rounded-full bg-primary/5 blur-[120px]" />
        <div className="absolute bottom-[-10%] right-[-5%] w-[30%] h-[30%] rounded-full bg-primary/5 blur-[100px]" />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-5xl font-bold mb-4 tracking-tight">
            Reach Our <span className="text-primary">Team</span>
          </h2>
          <p className="text-lg text-muted-foreground">
            Ready to turn your junk car into cash? Contact us today for a free, no-obligation quote and schedule your same-day pickup anywhere in BC.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          
          <div className="lg:col-span-5 space-y-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-4">
              <Card className="bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/30 transition-colors shadow-lg">
                <CardContent className="p-6 flex items-start gap-4">
                  <div className="p-3 bg-primary/10 rounded-lg shrink-0">
                    <Phone className="size-6 text-primary" />
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-1">Call Us 24/7</h4>
                    <Link to={`tel:778-385-3007`} className="text-xl font-bold text-foreground hover:text-primary transition-colors">
                      {CONTACT_INFO.phone}
                    </Link>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/30 transition-colors shadow-lg">
                <CardContent className="p-6 flex items-start gap-4">
                  <div className="p-3 bg-primary/10 rounded-lg shrink-0">
                    <Mail className="size-6 text-primary" />
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-1">Email Us</h4>
                    <Link to={`mailto:JunkCarPickUpBC@gmail.com`} className="text-lg font-bold text-foreground hover:text-primary transition-colors break-all">
                      {CONTACT_INFO.email}
                    </Link>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card className="bg-card/50 backdrop-blur-sm border-border/50 overflow-hidden shadow-lg">
              <div className="p-4 border-b border-border/50 flex items-center gap-2 bg-muted/20">
                <MapPin className="size-5 text-primary" />
                <h4 className="font-semibold text-foreground">Service Area: {CONTACT_INFO.address}</h4>
              </div>
              <div className="h-[300px] w-full relative bg-muted">
                <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d5323181.181818182!2d-125.0!3d54.0!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x5371676241c24d8d%3A0x10b0b0b0b0b0b0b0!2sBritish%20Columbia%2C%20Canada!5e0!3m2!1sen!2sus!4v1620000000000!5m2!1sen!2sus" className="absolute inset-0 w-full h-full border-0 filter grayscale contrast-125 opacity-80 mix-blend-luminosity" allowFullScreen={false} loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Service Area Map"></iframe>
                <div className="absolute inset-0 bg-background/20 pointer-events-none"></div>
              </div>
            </Card>
          </div>

          <div className="lg:col-span-7">
            <Card className="bg-card/80 backdrop-blur-md border-border/50 shadow-2xl relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-primary to-transparent opacity-50"></div>
              
              <CardContent className="p-8 md:p-10">
                <div className="mb-8">
                  <h3 className="text-2xl font-bold text-foreground mb-2">Get Your Instant Cash Offer</h3>
                  <p className="text-muted-foreground">Fill out the details below and we'll get back to you with a quote immediately.</p>
                </div>

                <FormProvider formId="reach_team_form_contact" sectionName="Reach Our Team" emailRecipient="JunkCarPickUpBC@gmail.com" defaultValues={{
                name: "",
                email: "",
                phone: "",
                service_interest: "",
                message: ""
              }} validationRules={{
                name: {
                  required: true
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                phone: {
                  required: true,
                  pattern: "phone"
                },
                service_interest: {
                  required: true
                }
              }} submitText="Get Instant Quote" successMessage="Thank you! Your request has been received. Our team will contact you shortly with your cash offer.">
                  <ContactFormInner />
                </FormProvider>
              </CardContent>
            </Card>
          </div>

        </div>
      </div>
    </section>;
}